import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import Dialog from '../common/dialog'
import { requestAccessToken } from '../../api/auth.api'
import { CustomerCompanyFunding, FormError } from '../../types'
import { AuthContext, NotificationsContext } from '../../contexts'
import { revertCustomerCompanyFunding } from '../../api/customers.api'

export const RevertCustomerCompanyFundingModal = ({
  selectedCustomerCompanyFunding,
  handleSetModalOpenState,
  handleCloseFundingAllocationModal
}: {
  selectedCustomerCompanyFunding?: CustomerCompanyFunding
  handleSetModalOpenState: (openState: boolean) => void
  handleCloseFundingAllocationModal: () => void
}) => {
  const navigate = useNavigate()
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['customer-company-details-management', 'common'])

  const [errors, setErrors] = useState<FormError>({})
  const [dialogIsLoading, setDialogIsLoading] = useState<boolean>(false)

  const handleRevertCustomerCompanyFunding = async () => {
    setErrors({})

    try {
      setDialogIsLoading(true)

      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await revertCustomerCompanyFunding(String(selectedCustomerCompanyFunding?._id), accessTokenData.data.accessToken)
      handleSetModalOpenState(false)
      handleCloseFundingAllocationModal()
      updateNewNotificationStatus?.(true, 'reject-funding-allocation-success')
    } catch (error: any) {
      console.log(error, error?.response?.status, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 406 && error?.response?.data?.message === 'Processing operation') {
        setErrors({ generalError: t('formGeneralErrors.processingOperation', { ns: 'common' }) })
      } else if (error?.response?.status === 406 && error?.response?.data?.message === 'Insufficient amount') {
        setErrors({ generalError: t('formGeneralErrors.insufficientAmount', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogIsLoading(false)
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <Dialog
      actionButtonWarningStyle
      dialogIsLoading={dialogIsLoading}
      dialogTitle={t('revertFundingModal.title')}
      handleDialogAction={() => handleRevertCustomerCompanyFunding()}
      handleCloseOptionAction={() => {
        handleSetModalOpenState(false)
      }}
      actionButtonText={t('fundingDetailsModal.actionButtonRevertText')}
      handleDialogOpenState={(openState: boolean) => handleSetModalOpenState(openState)}
    >
      <div className="customer-company-funding-rejection-main-instruction">{t('revertFundingModal.reversionMainInstruction')}</div>
      {errors?.generalError && (
        <div
          className="error-message"
          style={{ margin: '20px 0px 0px' }}
        >
          {errors?.generalError}
        </div>
      )}
    </Dialog>
  )
}
